import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import { toastSuccess, toastError } from '../../utils/alertHelper';

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("email");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [userObj, setUserObj] = useState(null);
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const otpLength = 4;
  const navigate = useNavigate();

  const normalizeProfilePic = (profilepic) => {
    if (!profilepic) return "";
    const s = String(profilepic).trim();
    if (s.startsWith("http") || s.startsWith("data:")) return s;
    return `${IMAGE_BASE_URL}${s.replace(/^\/+/, "")}`;
  };

  const storeUserToLocal = (user, token = "", auth = "true") => {
    try {
      localStorage.setItem("email", user.email || "");
      localStorage.setItem("firstName", user.firstName || "");
      localStorage.setItem("lastName", user.lastName || "");
      // store profilepic as normalized URL (helps components show image immediately)
      localStorage.setItem("profilepic", normalizeProfilePic(user.profilepic || ""));
      localStorage.setItem("role", user.role?.name || "");
      localStorage.setItem("roleId", user.role?._id || "");
      localStorage.setItem("userId", user._id || user.id || "");
      localStorage.setItem("user", JSON.stringify(user));
      if (token) localStorage.setItem("token", token);
      localStorage.setItem("auth", auth);
      localStorage.setItem("userType", user.userType || "");

      if (user.role?.permissionGroups) {
        localStorage.setItem("permissionGroups", JSON.stringify(user.role.permissionGroups));
      }

      if (user.business) {
        localStorage.setItem("businessId", user.business._id || "");
        localStorage.setItem("businessName", user.business.name || "");
        localStorage.setItem("businessEmail", user.business.email || "");
        localStorage.setItem("businessAddress", user.business.address || "");
        localStorage.setItem("businessBuildingNo", user.business.buildingno || "");
        localStorage.setItem("businessBuildingName", user.business.buildingName || "");
        localStorage.setItem("businessLandmark", user.business.landmark || "");
        localStorage.setItem("businessArea", user.business.area || "");
        localStorage.setItem("businessMobileNumber", user.business.mobileNumber || "");
        localStorage.setItem("businessGstNumber", user.business.gstNumber || "");
        localStorage.setItem("businessPincode", user.business.pincode || "");
        localStorage.setItem("businessCin", user.business.cin || "");
        localStorage.setItem("businessPan", user.business.pan || "");
        localStorage.setItem("businessTan", user.business.tan || "");
      }

      if (user.location && Array.isArray(user.location.coordinates)) {
        localStorage.setItem("locationType", user.location.type || "");
        localStorage.setItem("locationCoordinates", JSON.stringify(user.location.coordinates));
      }

      if (user.addedBy) {
        localStorage.setItem("addedById", user.addedBy._id || "");
        localStorage.setItem("addedByFirstName", user.addedBy.firstName || "");
        localStorage.setItem("addedByLastName", user.addedBy.lastName || "");
        localStorage.setItem("addedByEmail", user.addedBy.email || "");
        localStorage.setItem("addedByRole", user.addedBy.role?.name || "");
      }

      // Notify other components in same tab
      try {
        window.dispatchEvent(new Event('userChanged'));
      } catch (e) {
        // ignore older browsers
      }
    } catch (err) {
      console.error("storeUserToLocal error:", err);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post("/api/user/login", { email });
      const { Status, user, Message } = response.data;
      if (Status === 1 && user && user.role?.name) {
        setUserRole(user.role.name);
        setUserObj(user);

        // Save user details to localStorage (auth false for now)
        storeUserToLocal(user, "", "false");

        if (user.role.name === "SuperAdmin") {
          setStep("password");
        } else {
          setStep("otp");
        }
      } else {
        toastError(Message || "Email not found");
      }
    } catch (error) {
      toastError("Failed to check role");
    }
    setIsSubmitting(false);
  };

  // Step 2: SuperAdmin password login
  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post("/api/user/login", { email, password });
      const {
        Status,
        Message,
        user,
        UserToken,
        defaultCountry,
        defaultState,
        defaultCity,
        device
      } = response.data;

      if (Status === 1 && user) {
        storeUserToLocal(user, UserToken || "", "true");

        if (defaultCountry) {
          localStorage.setItem("defaultCountry", defaultCountry._id || "");
          localStorage.setItem("defaultCountryName", defaultCountry.name || "");
        }
        if (defaultState) {
          localStorage.setItem("defaultState", defaultState._id || "");
          localStorage.setItem("defaultStateName", defaultState.name || "");
        }
        if (defaultCity) {
          localStorage.setItem("defaultCity", defaultCity._id || "");
          localStorage.setItem("defaultCityName", defaultCity.name || "");
        }
        if (device?.status && device.response) {
          localStorage.setItem("deviceId", device.response.deviceId || "");
          localStorage.setItem("devicetoken", device.response.devicetoken || "");
        }
        toastSuccess(Message || "Login successful");
        localStorage.setItem("checkRole", "SuperAdmin");
        try { window.dispatchEvent(new Event('userChanged')); } catch (e) {}
        navigate("/dashboard");
      } else {
        toastError(Message || "Invalid email or password");
      }
    } catch (error) {
      const backendMsg = error.response?.data?.Message;
      toastError(backendMsg || "Something went wrong, please try again");
    }
    setIsSubmitting(false);
  };

  // Step 3: OTP verify for other roles
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post("/api/user/verifyOtp", { email, otp });
      const { Status, Message, user, UserToken, defaultCountry, defaultState, defaultCity, device } = response.data;
      if (Status === 1 && user) {
        storeUserToLocal(user, UserToken || "", "true");

        if (defaultCountry) {
          localStorage.setItem("defaultCountry", defaultCountry._id || "");
          localStorage.setItem("defaultCountryName", defaultCountry.name || "");
        }
        if (defaultState) {
          localStorage.setItem("defaultState", defaultState._id || "");
          localStorage.setItem("defaultStateName", defaultState.name || "");
        }
        if (defaultCity) {
          localStorage.setItem("defaultCity", defaultCity._id || "");
          localStorage.setItem("defaultCityName", defaultCity.name || "");
        }
        if (device?.status && device.response) {
          localStorage.setItem("deviceId", device.response.deviceId || "");
          localStorage.setItem("devicetoken", device.response.devicetoken || "");
        }
        toastSuccess(Message || "OTP verified, login successful");
        if (user.userType === "Admin") {
          localStorage.setItem("checkRole", "SuperAdmin");
          try { window.dispatchEvent(new Event('userChanged')); } catch (e) {}
          navigate("/admin");
        } else if (user.userType === "Company") {
          localStorage.setItem("checkRole", "Company Admin");
          try { window.dispatchEvent(new Event('userChanged')); } catch (e) {}
          navigate("/user");
        } else {
          try { window.dispatchEvent(new Event('userChanged')); } catch (e) {}
          navigate("/");
        }
      } else {
        toastError(Message || "Invalid OTP");
      }
    } catch (error) {
      toastError("Failed to verify OTP");
    }
    setIsSubmitting(false);
  };

  // OTP helpers (unchanged)
  const handleOtpDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);
    if (value && index < otpLength - 1) {
      document.getElementById(`otp-input-${index + 1}`)?.focus();
    }
    setOtp(newDigits.join(""));
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");
    if (paste.length === otpLength) {
      const digitsArr = paste.split("").slice(0, otpLength);
      setOtpDigits(digitsArr);
      setOtp(digitsArr.join(""));
      document.getElementById(`otp-input-${otpLength - 1}`)?.focus();
      e.preventDefault();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`)?.focus();
    }
  }

  return (
    <section className="user-login section">
      <div className="container">
        <div className="row">
          <div className="col-12">
            <div className="block">
              <div className="image align-self-center">
                <img className="img-fluid" src='/front-end/images/Login/login.jpg' alt="desk-image" />
              </div>
              <div className="content text-center">
                <div className="logo">
                  <a href="#"><img src='/front-end/images/solar-final.png' alt="" height="50px" width="150px" /></a>
                </div>
                <div className="title-text">
                  <h3>Sign in Your Account</h3>
                </div>
                {step === "email" && (
                  <form onSubmit={handleEmailSubmit} className="mx-auto mt-4" style={{ maxWidth: "400px" }}>
                    <div className="mb-3">
                      <label htmlFor="email" className="form-label text-start d-block"
                        style={{ color: "#667085" }}
                      >
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        placeholder="Enter your email"
                        onChange={(e) => setEmail(e.target.value)}
                        value={email}
                        required
                        disabled={isSubmitting}
                        className="form-control rounded-3"
                        style={{
                          backgroundColor: "#F6F6F6",
                          borderColor: "#BBBFD2",
                          color: "#3F3F46",
                        }}
                      />
                    </div>

                    <div className="d-grid">
                      <button
                        type="submit"
                        className="btn btn-lg rounded-3 text-white"
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: "#5766A1",
                          border: "#FFFFFF",
                        }}
                      >
                        {isSubmitting ? "Sending..." : "Log In"}
                      </button>
                    </div>

                  </form>

                )}
                {step === "password" && (
                  <form onSubmit={handlePasswordLogin}>
                    {/* Password Field */}
                    <div className="mb-3">
                      <label
                        htmlFor="password"
                        className="form-label text-start d-block"
                        style={{ color: "#667085" }}
                      >
                        Password
                      </label>
                      <input
                        type="password"
                        id="password"
                        placeholder="Enter your password"
                        onChange={(e) => setPassword(e.target.value)}
                        value={password}
                        required
                        disabled={isSubmitting}
                        className="form-control rounded-3"
                        style={{
                          backgroundColor: "#F6F6F6",
                          borderColor: "#BBBFD2",
                          color: "#3F3F46",
                        }}
                      />
                    </div>

                    {/* Login Button */}
                    <div className="d-grid">
                      <button
                        type="submit"
                        className="btn rounded-3 text-white"
                        disabled={isSubmitting}
                        style={{
                          backgroundColor: "#5766A1",
                          border: "none",
                          fontSize: "14px",
                          padding: "8px 0",
                        }}
                      >
                        {isSubmitting ? "Logging In..." : "Log In"}
                      </button>
                    </div>
                  </form>
                )}
                {step === "otp" && (
                  <form onSubmit={handleOtpVerify}>
                    <div style={{ display: "flex", justifyContent: "center", gap: "10px", marginBottom: "16px" }}>
                      {otpDigits.map((digit, idx) => (
                        <input
                          key={idx}
                          id={`otp-input-${idx}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={e => handleOtpDigitChange(idx, e.target.value)}
                          onKeyDown={e => handleOtpKeyDown(idx, e)}
                          onPaste={handleOtpPaste}
                          className="form-control"
                          style={{ width: "40px", textAlign: "center", fontSize: "24px" }}
                          disabled={isSubmitting}
                          autoFocus={idx === 0}
                        />
                      ))}
                    </div>
                    <div>
                      <button type="submit" className="btn btn-main-sm mt-4" disabled={isSubmitting || otp.length < otpLength}>
                        Verify OTP
                      </button>
                    </div>
                  </form>
                )}
                <div className="new-acount">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Login;